/**
 * useChatWebSocket Hook
 *
 * Connects to /api/sse/chat?spaceId= for real-time chat message delivery.
 * Creates one EventSource per spaceId and invalidates React Query
 * caches when new messages arrive.
 */

import { useEffect, useRef } from 'react';
import { queryClient } from '@/lib/query-client';

export interface ChatTypingEvent {
  spaceId: string;
  agentId: string;
  agentName: string;
  isTyping: boolean;
}

export interface ChatTokenEvent {
  spaceId: string;
  agentId: string;
  token: string;
}

export function useChatWebSocket(
  spaceIds: string[],
  onTyping?: (event: ChatTypingEvent) => void,
  onMessage?: (spaceId: string, senderId: string) => void,
  onToken?: (event: ChatTokenEvent) => void,
) {
  const seenIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (spaceIds.length === 0) return;
    seenIdsRef.current.clear();

    const sources = spaceIds.map((spaceId) => {
      const es = new EventSource(
        `/api/sse/chat?spaceId=${encodeURIComponent(spaceId)}`,
        { withCredentials: true },
      );

      es.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          const eventType = payload.type;

          if (eventType === 'chat:typing') {
            onTyping?.(payload.data as ChatTypingEvent);
            return;
          }

          if (eventType === 'chat:token') {
            onToken?.(payload.data as ChatTokenEvent);
            return;
          }

          if (eventType === 'chat:reaction') {
            const sid = payload.data?.spaceId || payload.spaceId;
            if (sid) {
              queryClient.invalidateQueries({ queryKey: [`/api/chat/spaces/${encodeURIComponent(sid)}/messages`] });
            }
            return;
          }

          if (eventType === 'chat:memory_cleared') {
            const sid = payload.data?.spaceId || payload.spaceId;
            if (sid) {
              queryClient.invalidateQueries({ queryKey: [`/api/chat/spaces/${encodeURIComponent(sid)}/messages`] });
            }
            return;
          }

          const sid = payload.data?.spaceId || payload.data?.data?.spaceId;
          if (
            (eventType === 'chat:message' || eventType === 'chat:message:edited' || eventType === 'chat:message:deleted') &&
            sid
          ) {
            if (eventType === 'chat:message') {
              const msgId = payload.data?.id ?? payload.data?.data?.id;
              if (msgId) {
                if (seenIdsRef.current.has(msgId)) return;
                seenIdsRef.current.add(msgId);
                if (seenIdsRef.current.size > 200) {
                  const oldest = seenIdsRef.current.values().next().value;
                  if (oldest !== undefined) seenIdsRef.current.delete(oldest);
                }
              }
            }
            queryClient.invalidateQueries({ queryKey: [`/api/chat/spaces/${encodeURIComponent(sid)}/messages`] });
            queryClient.invalidateQueries({ queryKey: ['/api/chat/spaces'] });
            queryClient.invalidateQueries({ queryKey: ['/api/chat/unread-count'] });
            if (eventType === 'chat:message' && onMessage) {
              const senderId = payload.data?.senderId ?? payload.data?.data?.senderId;
              onMessage(sid, senderId);
            }
          }
        } catch { /* ignore */ }
      };

      return es;
    });

    return () => {
      sources.forEach((es) => es.close());
      seenIdsRef.current.clear();
    };
  }, [spaceIds.join(',')]);
}
